// Unsaved edits. An editor whose text differs from what it started from
// registers while it does; the guard asks before an in-app navigation, the
// back button or closing the page would throw that text away, and the
// editors ask the same question before Cancel or Esc does. Each edit belongs
// to the page it is drawn on, the page under Settings or the sheet's own
// section, and only leaving that page asks.
import { useEffect, useInsertionEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import { isSheet, pageOf, pagesLeft } from '../lib/keys'
import { useSurface, useTabScope } from '../workspace/context'

export const DISCARD_QUESTION = 'Discard your unsaved changes?'

/** Each unsaved edit and its page; null where it is drawn outside the app's pages, left by any move. */
const dirty = new Map<symbol, string | null>()

/**
 * Said on <html> too, as `data-unsaved`: the Mac app reads it, and asks the
 * same question before it closes a tab or a pane (macos/Sources/BoardWindow.swift).
 */
function mark(): void {
  document.documentElement.toggleAttribute('data-unsaved', dirty.size > 0)
}

/** True while any editor holds text it has not saved, or one of these pages' editors. */
export function hasUnsavedChanges(pages?: readonly string[]): boolean {
  if (!pages) return dirty.size > 0
  return [...dirty.values()].some(page => page === null || pages.includes(page))
}

/** Register the calling editor as holding unsaved text while `isDirty`. */
export function useUnsavedChanges(isDirty: boolean): void {
  const scope = useTabScope()
  const surface = useSurface()
  const page = surface !== null ? pageOf(surface) : scope ? pageOf(new URL(scope.path(), 'http://workspace.invalid').pathname) : null
  const token = useRef(Symbol('unsaved edit')).current
  // Insertion lifetimes end on real unmount, unlike layout/passive effects
  // disconnected by Activity. Hidden drafts remain registered for close guards.
  useInsertionEffect(() => {
    if (!isDirty) return
    dirty.set(token, page)
    scope?.dirty(token, true, page ?? undefined)
    mark()
    return () => {
      dirty.delete(token)
      scope?.dirty(token, false)
      mark()
    }
  }, [isDirty, token, scope, page])
}

/** Ask before discarding; true when the text may go. */
export function confirmDiscard(): boolean {
  return window.confirm(DISCARD_QUESTION)
}

/**
 * Mounted once, inside the router: holds a navigation to another page (a
 * link, ⌘K, the back button) while an editor of the page it leaves has
 * unsaved text, and asks. A change of hash or query on the same page keeps
 * the editor, so it passes; so does opening Settings over it, and moving or
 * closing the sheet back to it. `under` is the page drawn under the sheet.
 */
export function UnsavedChangesGuard({ under }: { under?: string }) {
  const scope = useTabScope()
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const from = currentLocation.pathname
    const left = pagesLeft(from, nextLocation.pathname, under ?? (isSheet(from) ? '/' : from))
    return left.length > 0 && (scope ? scope.hasUnsaved(left) : hasUnsavedChanges(left))
  })

  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (confirmDiscard()) blocker.proceed()
    else blocker.reset()
  }, [blocker])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges()) return
      event.preventDefault()
      // Older WebKit asks only when returnValue is set.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  return null
}
